import React, { useState, useEffect } from 'react';
import { X, Clock, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSubscription } from '@/contexts/SubscriptionContext';
import axios from 'axios';
import { API_URL as API } from '@/utils/apiConfig';

export function TrialStatusBadge() {
  const { t } = useTranslation();
  const { subscription } = useSubscription();
  
  if (!subscription?.is_trial || subscription?.trial_days_remaining === null) {
    return null;
  }
  
  const daysLeft = subscription.trial_days_remaining;
  const isUrgent = daysLeft <= 3;
  
  return (
    <div 
      className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
        isUrgent ? 'bg-amber-500/15 text-amber-400' : 'bg-blue-500/15 text-blue-400'
      }`}
      data-testid="trial-status-badge"
    >
      <Clock className="h-3 w-3" />
      <span>{daysLeft === 1 ? t('trial.days_left', { count: daysLeft }) : t('trial.days_left_plural', { count: daysLeft })}</span>
    </div>
  );
}

export function TrialPopup() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { subscription, tier } = useSubscription();
  const [config, setConfig] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [show, setShow] = useState(false);

  // Fetch popup config
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const res = await axios.get(`${API}/app-config/trial-popup`, { withCredentials: true });
        setConfig(res.data);
      } catch {
        // Use defaults if config not available
        setConfig({
          enabled: true,
          trigger_at_days_remaining: 3,
          title: t('trial.ending_soon'),
          message: t('trial.ending_message', { days: '{days}' }),
          button_text: t('trial.upgrade_now'),
          show_dismiss: true,
          style: "modal"
        });
      }
    };
    fetchConfig();
  }, []);

  // Check if should show popup
  useEffect(() => {
    if (!config || !subscription?.is_trial || dismissed) return;
    
    const daysLeft = subscription.trial_days_remaining;
    const dismissKey = `trial_popup_dismissed_${tier}_${daysLeft}`;
    
    // Check if already dismissed for this day count
    if (localStorage.getItem(dismissKey)) return;
    
    // Show if days remaining is at or below trigger threshold
    if (config.enabled && daysLeft !== null && daysLeft <= config.trigger_at_days_remaining) {
      // Small delay to not show immediately
      const timer = setTimeout(() => setShow(true), 2000);
      return () => clearTimeout(timer);
    }
  }, [config, subscription, dismissed, tier]);

  const handleDismiss = () => {
    const daysLeft = subscription?.trial_days_remaining;
    localStorage.setItem(`trial_popup_dismissed_${tier}_${daysLeft}`, 'true');
    setDismissed(true);
    setShow(false);
  };

  const handleUpgrade = () => {
    setShow(false);
    navigate('/plans');
  };

  if (!show || !config) return null;

  const daysLeft = subscription?.trial_days_remaining ?? 0;
  const message = config.message.replace('{days}', daysLeft.toString());

  // Modal style
  if (config.style === 'modal') {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" data-testid="trial-popup-modal">
        <div 
          className="relative w-full max-w-sm p-6 rounded-2xl shadow-xl"
          style={{ background: 'var(--theme-surface)', border: '1px solid var(--theme-border)' }}
        >
          {config.show_dismiss && (
            <button
              onClick={handleDismiss}
              className="absolute top-4 right-4 p-1 rounded-full hover:bg-white/10"
              data-testid="trial-popup-dismiss"
            >
              <X className="h-4 w-4" style={{ color: 'var(--theme-textSecondary)' }} />
            </button>
          )}
          
          <div className="flex flex-col items-center text-center">
            <div className="w-14 h-14 rounded-full flex items-center justify-center mb-4" style={{ background: 'rgba(251,191,36,0.15)' }}>
              <Sparkles className="h-7 w-7 text-amber-400" />
            </div>
            
            <h3 className="text-lg font-semibold mb-2" style={{ color: 'var(--theme-text)' }}>
              {config.title}
            </h3>
            
            <p className="text-sm mb-6" style={{ color: 'var(--theme-textSecondary)' }}>
              {message}
            </p>
            
            <Button
              onClick={handleUpgrade}
              className="w-full py-3 rounded-xl font-medium"
              style={{ background: 'linear-gradient(135deg, #fbbf24, #f59e0b)', color: '#000' }}
              data-testid="trial-popup-upgrade"
            >
              {config.button_text}
            </Button>
            
            {config.show_dismiss && (
              <button
                onClick={handleDismiss}
                className="mt-3 text-xs"
                style={{ color: 'var(--theme-textSecondary)' }}
              >
                {t('trial.maybe_later')}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Banner style
  if (config.style === 'banner') {
    return (
      <div 
        className="fixed top-0 left-0 right-0 z-50 p-3 flex items-center justify-between gap-3"
        style={{ paddingTop: 'var(--safe-area-top, env(safe-area-inset-top, 0px))', background: 'linear-gradient(135deg, #fbbf24, #f59e0b)' }}
        data-testid="trial-popup-banner"
      >
        <div className="flex items-center gap-2">
          <Clock className="h-4 w-4 text-slate-900" />
          <span className="text-sm font-medium text-slate-900">{message}</span>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={handleUpgrade}
            size="sm"
            className="bg-slate-900 text-white hover:bg-slate-800 text-xs px-3"
          >
            {config.button_text}
          </Button>
          {config.show_dismiss && (
            <button onClick={handleDismiss} className="p-1">
              <X className="h-4 w-4 text-slate-900" />
            </button>
          )}
        </div>
      </div>
    );
  }

  // Toast style (bottom)
  return (
    <div 
      className="fixed bottom-4 left-4 right-4 z-50 p-4 rounded-2xl shadow-lg flex items-center gap-3"
      style={{ background: 'rgba(15,23,42,0.95)', border: '1px solid rgba(255,255,255,0.1)', maxWidth: '400px', margin: '0 auto' }}
      data-testid="trial-popup-toast"
    >
      <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: 'rgba(251,191,36,0.15)' }}>
        <Clock className="h-5 w-5 text-amber-400" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-white truncate">{config.title}</p>
        <p className="text-xs text-slate-400">{daysLeft === 1 ? t('trial.days_left', { count: daysLeft }) : t('trial.days_left_plural', { count: daysLeft })}</p>
      </div>
      <Button
        onClick={handleUpgrade}
        size="sm"
        className="shrink-0 text-xs px-3"
        style={{ background: 'linear-gradient(135deg, #fbbf24, #f59e0b)', color: '#000' }}
      >
        {t('trial.upgrade_now')}
      </Button>
      {config.show_dismiss && (
        <button onClick={handleDismiss} className="p-1 text-slate-500 hover:text-white">
          <X className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}

export default TrialPopup;
